/* eslint-disable react-hooks/set-state-in-effect, react-hooks/preserve-manual-memoization, react/no-unescaped-entities --
 * v77.99.77: This file uses idiomatic React patterns (effect-driven
 * state hydration after fetches, English text with apostrophes /
 * quotes in JSX). The experimental React 19 hooks rules
 * (`set-state-in-effect`, `purity`, `preserve-manual-memoization`)
 * are not shipped by eslint-plugin-react-hooks 5.2.0 — only the
 * standalone Emergent lint tool surfaces them, and they mis-flag
 * these intentional patterns. `react/no-unescaped-entities` similarly
 * flags safe English text.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { downloadFile } from "@/lib/downloadFile";
import { recordThirdPartyView } from "@/lib/thirdPartyView";
import { useAuth } from "@/context/AuthContext";
import { useChildScope } from "@/context/ChildScopeContext";
import { Button } from "@/components/ui/button";
import { UpgradePrompt } from "@/components/UpgradePrompt";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { VoiceDictateButton } from "@/components/journal/VoiceDictateButton";
import { Download, Send, Lock, Flag, FlagOff, Info, Search, X, Users } from "lucide-react";
import { ChildDots } from "@/components/ChildDots";
import { ChildPicker, validateChildSelection } from "@/components/ChildPicker";
import { getParentColorById } from "@/lib/parentColor";
import { formatDT } from "@/lib/format";
import { toast } from "sonner";
import { useIdempotencyKey, idempotencyHeader } from "@/lib/idempotency";

// Highlight occurrences of `term` in `text` as an array of React nodes.
function highlightText(text, term) {
  if (!term) return text;
  const needle = term.trim();
  if (!needle) return text;
  const parts = [];
  const lower = (text || "").toLowerCase();
  const lowerNeedle = needle.toLowerCase();
  let i = 0;
  let idx = lower.indexOf(lowerNeedle, i);
  while (idx !== -1) {
    if (idx > i) parts.push(text.slice(i, idx));
    parts.push(<mark key={parts.length} className="bg-[#FBF0DC] text-[#5C3A1E] rounded-sm px-0.5">{text.slice(idx, idx + needle.length)}</mark>);
    i = idx + needle.length;
    idx = lower.indexOf(lowerNeedle, i);
  }
  if (i < text.length) parts.push(text.slice(i));
  return parts;
}

// Silent, client-side heuristic to surface wording the co-parent may read as hostile.
// We only flag when whole-word matches appear; we never block sending.
const HOSTILE_PATTERNS = [
  { re: /\b(always|never)\b/i, hint: '"always" / "never" can feel accusatory — consider the specific instance.' },
  { re: /\b(you|your)\s+(are|'re|'ve)\s+(so\s+)?(stupid|lazy|selfish|terrible|useless|awful|pathetic|ridiculous|rubbish|crap)\b/i, hint: "Personal criticism — try naming the behaviour, not the person." },
  { re: /\b(shut\s*up|stfu|piss\s*off|f\*?\*?\*?k|fuck|fucking|shit|bastard|bitch|asshole|arsehole|moron|idiot|jerk|dick)\b/i, hint: "Strong language — consider a calmer phrasing." },
  { re: /\b(hate|hating)\s+(you|u)\b/i, hint: '"Hate you" — consider naming what you disagree with.' },
  { re: /\b(you're|youre|you are)\s+(lying|pathetic|ridiculous|unfit|toxic|abusive)\b/i, hint: "Labelling language — stick to observable facts." },
  { re: /!{3,}/, hint: "Multiple exclamation marks can read as aggressive." },
  { re: /\b[A-Z]{4,}\b/, hint: "ALL-CAPS reads as shouting — lowercase keeps it calmer." },
];

export default function Messages() {
  const { user, isMediator, members } = useAuth();
  const { selectedChildId, children: scopeChildren, setSelectedChildId } = useChildScope();
  // v77.49 — Audit trail for third-party viewers. No-op for parents.
  useEffect(() => {
    recordThirdPartyView({ resource_type: "messages", resource_label: "Opened messages page" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [msgs, setMsgs] = useState([]);
  const [flagged, setFlagged] = useState([]);
  const [body, setBody] = useState("");
  const [msgChildIds, setMsgChildIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [flagFor, setFlagFor] = useState(null);
  const [flagReason, setFlagReason] = useState("");
  const [search, setSearch] = useState("");
  const [exportOpen, setExportOpen] = useState(false);
  const [exportScope, setExportScope] = useState("full"); // full | 7d | 30d | custom
  const [exportFrom, setExportFrom] = useState("");
  const [exportTo, setExportTo] = useState("");
  const [exportSender, setExportSender] = useState("all");
  const [exportFlaggedOnly, setExportFlaggedOnly] = useState(false);
  const [exportIncludeSearch, setExportIncludeSearch] = useState(true);
  const listRef = useRef(null);
  const textareaRef = useRef(null);

  // v77.21.9 — Auto-grow composer textarea. Mobile starts at the
  // compact 44px height; as the user types more than one line the
  // textarea expands up to ~6 rows (≈ 144px), after which an internal
  // scroll kicks in so the composer never crowds out the message
  // list. Resets back to its base height after a successful send.
  const autoSizeTextarea = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const max = 144; // ~6 lines on mobile
    el.style.height = `${Math.min(el.scrollHeight, max)}px`;
    el.style.overflowY = el.scrollHeight > max ? "auto" : "hidden";
  }, []);

  useEffect(() => {
    autoSizeTextarea();
  }, [body, autoSizeTextarea]);

  const toneHints = useMemo(() => {
    const text = body.trim();
    if (text.length < 2) return [];
    const hits = [];
    for (const p of HOSTILE_PATTERNS) {
      if (p.re.test(text) && !hits.includes(p.hint)) hits.push(p.hint);
    }
    return hits.slice(0, 3);
  }, [body]);

  const load = useCallback(async () => {
    const url = selectedChildId ? `/messages?child_id=${encodeURIComponent(selectedChildId)}` : "/messages";
    const [all, fl] = await Promise.all([api.get(url), api.get("/messages/flagged")]);
    setMsgs(all.data);
    setFlagged(fl.data);
    // v77.99.69 — Mediators are read-only; the per-message read receipt
    // endpoint is gated by `require_member` and would 403. Skip the
    // mark-read fan-out entirely so the network tab stays clean.
    if (isMediator) return;
    const toMark = all.data.filter((m) => !(m.read_by || []).includes(user.id));
    await Promise.all(toMark.map((m) => api.post(`/messages/${m.id}/read`).catch(() => {})));
  }, [user?.id, selectedChildId, isMediator]);

  useEffect(() => {
    load();
    const i = setInterval(load, 6000);
    return () => clearInterval(i);
  }, [load]);

  useEffect(() => {
    const el = listRef.current;
    if (el) {
      // Scroll the list itself (not the window). Prevents document scroll
      // leaking when flex ancestors don't clip properly.
      el.scrollTop = el.scrollHeight;
    }
  }, [msgs.length]);

  // Phase 3 Delivery B — explicit child selection for new messages.
  // When the user has a child filter pinned, mirror it. When in "All
  // children" view, start empty and require the user to pick before
  // sending.
  useEffect(() => {
    setMsgChildIds(selectedChildId ? [selectedChildId] : []);
  }, [selectedChildId]);

  const { key: sendKey, rotate: rotateSendKey } = useIdempotencyKey();

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    const validationErr = validateChildSelection({
      value: msgChildIds,
      hasChildren: (scopeChildren || []).length > 0,
      selectedChildId,
    });
    if (validationErr) { toast.error(validationErr); return; }
    setBusy(true);
    try {
      // v77.99.18 — Idempotency-Key on every chat send. A flaky retry
      // on a slow mobile connection must not produce two identical
      // messages in the thread; the backend collapses replays to the
      // original message.
      await api.post(
        "/messages",
        { body: body.trim(), child_ids: msgChildIds },
        { headers: idempotencyHeader(sendKey) },
      );
      rotateSendKey();
      setBody("");
      // Reset the picker back to whatever scope the user is in (so they
      // don't have to re-pick on every send when in master view they
      // tend to keep using the same children).
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send"); }
    finally { setBusy(false); }
  };

  const exportPdf = () => {
    // Default custom range to "today" when entering custom-mode
    setExportOpen(true);
  };

  // v77.35 — Messages must be a per-child experience. In "All children"
  // master mode we replace the entire list/composer/search/tabs surface
  // with a friendly prompt asking the user to pick a child. This keeps
  // exports, flag actions, search, and read-receipts strictly scoped to
  // a single child — preventing cross-child confusion in high-conflict
  // households where threads MUST stay separate.
  //
  // v77.99.61 — When the household has exactly one accessible child,
  // auto-select that child on entry so the user never sees the
  // "Choose a child" wall for no reason. Multi-child households still
  // get the prompt; that decision is genuinely ambiguous.
  const accessibleChildren = (scopeChildren || []).filter((c) => !c.archived_at);
  useEffect(() => {
    if (!selectedChildId && accessibleChildren.length === 1) {
      setSelectedChildId(accessibleChildren[0].id);
    }
  }, [selectedChildId, accessibleChildren.length, setSelectedChildId, accessibleChildren]);
  const needsChildSelection = !selectedChildId && accessibleChildren.length > 1;

  // Resolve `from_date` / `to_date` (YYYY-MM-DD, UTC) from the scope selector.
  // null → omit that bound from the query string.
  const resolveExportDates = () => {
    const today = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (exportScope === "7d") {
      const from = new Date(today); from.setUTCDate(from.getUTCDate() - 6);
      return { from: iso(from), to: iso(today) };
    }
    if (exportScope === "30d") {
      const from = new Date(today); from.setUTCDate(from.getUTCDate() - 29);
      return { from: iso(from), to: iso(today) };
    }
    if (exportScope === "custom") {
      return { from: exportFrom || null, to: exportTo || null };
    }
    return { from: null, to: null }; // full log
  };

  const doExport = () => {
    const qs = new URLSearchParams();
    const { from, to } = resolveExportDates();
    if (from) qs.set("from_date", from);
    if (to) qs.set("to_date", to);
    if (exportSender && exportSender !== "all") qs.set("sender_id", exportSender);
    if (exportFlaggedOnly) qs.set("flagged_only", "true");
    // Optional: propagate the on-screen search term so the PDF matches what the user sees.
    const needle = search.trim();
    if (exportIncludeSearch && needle) qs.set("q", needle);
    const url = `${API}/messages/export/pdf${qs.toString() ? `?${qs.toString()}` : ""}`;
    const today = new Date().toISOString().slice(0, 10);
    const fname = `messages-export-${today}.pdf`;
    // Iter 125.hybrid-exports — hybrid email fallback (see Expenses.jsx).
    downloadFile(url, fname, {
      label: "Generating messages PDF",
      successMessage: "PDF ready",
      asyncFallback: {
        endpoint: "/exports/messages/pdf/start",
        payload: {
          from_date: from || null,
          to_date: to || null,
          sender_id: (exportSender && exportSender !== "all") ? exportSender : null,
          flagged_only: !!exportFlaggedOnly,
          q: (exportIncludeSearch && needle) ? needle : null,
        },
      },
    });
    setExportOpen(false);
  };

  // Client-side search filter — shared between "All" and "Flagged" tabs.
  const filteredMsgs = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return msgs;
    return msgs.filter((m) => (m.body || "").toLowerCase().includes(needle));
  }, [msgs, search]);
  const filteredFlagged = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return flagged;
    return flagged.filter((m) => (m.body || "").toLowerCase().includes(needle));
  }, [flagged, search]);

  const submitFlag = async () => {
    try {
      await api.post(`/messages/${flagFor.id}/flag`, { reason: flagReason });
      toast.success("Flagged");
      setFlagFor(null); setFlagReason("");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const unflag = async (id) => {
    try { await api.post(`/messages/${id}/unflag`); toast.success("Unflagged"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const renderMessage = (m, inFlagged = false) => {
    const mine = m.sender_id === user.id;
    const myFlag = (m.flags || []).find((f) => f.by === user.id);
    const hasFlags = (m.flags || []).length > 0;
    const senderColor = getParentColorById(m.sender_id, members);
    const hasChildTags = (m.child_ids || []).length > 0;
    return (
      <div key={m.id} className={`flex ${mine && !inFlagged ? "justify-end" : "justify-start"} group`} data-testid={`message-${m.id}`}>
        <div
          className={`max-w-[88%] sm:max-w-[85%] md:max-w-[70%] border rounded-md p-3 overflow-hidden ${mine ? "text-white" : "bg-[#EEF3F4] border-[#D9E1E3] text-[#2E3A3F]"} ${hasFlags ? "ring-1 ring-[#825A41]" : ""}`}
          style={mine ? { backgroundColor: senderColor, borderColor: senderColor } : undefined}
        >
          {/* Metadata header — compact, theme-aware. Sender + date sit on
              the first line; child tags wrap to a second line on narrow
              widths so the bubble never overflows. */}
          <div className={`text-[11px] uppercase tracking-[0.1em] ${mine ? "text-white/85" : "text-[#6F828A]"} mb-1.5 font-mono-data`}>
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              {!mine && (
                <span
                  className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: senderColor }}
                  aria-hidden="true"
                />
              )}
              <span className="truncate">{m.sender_name}</span>
              <span className="opacity-70">·</span>
              <span className="whitespace-nowrap">{formatDT(m.created_at)}</span>
              {(m.read_by || []).length > 1 && <span className="opacity-70">· Read</span>}
              {hasFlags && <span className={`px-1.5 py-0.5 border rounded-sm text-[10px] ${mine ? "border-white/40 text-white" : "border-[#825A41] bg-[#F0E8E3] text-[#4A3A2E]"}`}>Flagged</span>}
            </div>
            {/* v77.21.5 — Child tags moved to their own row so they
                never get stuck on the same line as the sender + date
                on narrow mobile bubbles. */}
            {hasChildTags && (
              <div className="mt-1">
                <ChildDots childIds={m.child_ids} />
              </div>
            )}
          </div>
          <div className="whitespace-pre-wrap text-sm leading-relaxed break-words">{highlightText(m.body || "", search)}</div>
          {hasFlags && (
            <div className={`mt-2 pt-2 border-t ${mine ? "border-white/20" : "border-[#D9E1E3]"} text-xs space-y-0.5`}>
              {(m.flags || []).map((f, i) => (
                <div key={`${f.by_name || "flag"}-${i}`} className={mine ? "text-white/80" : "text-[#6F828A]"}>
                  <span className="font-mono-data">{f.by_name}</span>{f.reason ? `: ${f.reason}` : " flagged this"}
                </div>
              ))}
            </div>
          )}
          {/* v77.21.5 — Flag action sits on its own row with a soft top
              divider so it always reads as part of THIS bubble and never
              looks detached or floating on mobile. */}
          <div className={`mt-2 pt-2 border-t ${mine ? "border-white/20" : "border-[#D9E1E3]"} flex justify-end`}>
            {myFlag ? (
              <button type="button" onClick={() => unflag(m.id)} className={`text-[10px] uppercase tracking-[0.1em] inline-flex items-center gap-1 ${mine ? "text-white/80 hover:text-white" : "text-[#6F828A] hover:text-[#2E3A3F]"}`} data-testid={`unflag-${m.id}`}>
                <FlagOff className="w-3 h-3" strokeWidth={1.5} /> Unflag
              </button>
            ) : (
              <button type="button" onClick={() => { setFlagFor(m); setFlagReason(""); }} className={`text-[10px] uppercase tracking-[0.1em] inline-flex items-center gap-1 ${mine ? "text-white/80 hover:text-white" : "text-[#6F828A] hover:text-[#2E3A3F]"}`} data-testid={`flag-${m.id}`}>
                <Flag className="w-3 h-3" strokeWidth={1.5} /> Flag as concerning
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    // v77.21.7 — Chat-style layout. The page is a FIXED-HEIGHT flex column
    // (so the list scrolls internally and the composer is always pinned to
    // the bottom of the page above the mobile bottom-nav). Height calc:
    //   mobile: 100dvh - mobile-header(3.5rem) - main pb-24(6rem) - py-6(3rem)
    //         + small buffer for occasional subscription/mediator banner.
    //   desktop: 100dvh - py-6(3rem) - small buffer.
    // `100dvh` (dynamic) auto-collapses when the on-screen keyboard opens
    // so the composer stays in view as the keyboard pushes up.
    <div className="flex flex-col h-[calc(100dvh-13rem)] lg:h-[calc(100dvh-6rem)] gap-2 sm:gap-4 overflow-hidden" data-testid="messages-page">
      {/* v77.21.8 — Title row is single-line on mobile to keep more room
          for the message list. The "Secure log" eyebrow + permanence
          notice are merged into a compact icon-tooltip on mobile. */}
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex items-center gap-2">
          <h1 className="font-heading text-xl sm:text-3xl font-medium truncate">Messages</h1>
          <span className="hidden sm:inline-flex text-[10px] uppercase tracking-[0.12em] text-[#6F828A] border border-[#D9E1E3] rounded-sm px-1.5 py-0.5">Secure log</span>
          <span
            className="sm:hidden inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] text-[#6F828A] border border-[#D9E1E3] rounded-sm px-1.5 py-0.5"
            title="Messages are permanent. They cannot be edited or deleted once sent."
            data-testid="messages-permanence-pill"
          >
            <Lock className="w-3 h-3" strokeWidth={1.75} /> Permanent
          </span>
        </div>
        {!needsChildSelection && (
          <Button variant="outline" size="sm" onClick={exportPdf} className="border-[#D9E1E3] rounded-sm hover:bg-[#E8F0F1] flex-shrink-0 h-8 sm:h-9 px-2 sm:px-3" data-testid="export-pdf-button">
            <Download className="w-4 h-4 sm:mr-2" strokeWidth={1.5} /> <span className="hidden sm:inline">Export PDF</span>
          </Button>
        )}
      </div>

      {needsChildSelection ? (
        // v77.35 — All-children empty state. Full-card prompt that
        // replaces the list, search, tabs, composer and export. Same
        // shape on desktop and mobile (the parent flex column collapses
        // the height naturally via flex-1).
        <div className="flex-1 min-h-0 flex items-center justify-center" data-testid="messages-pick-child-empty-state">
          <div className="max-w-md mx-auto bg-white border border-[#D9E1E3] rounded-md p-6 sm:p-8 text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-[#E8F0F1] inline-flex items-center justify-center mb-4">
              <Users className="w-5 h-5 text-[#1F9FA4]" strokeWidth={1.75} />
            </div>
            <h2 className="font-heading text-lg sm:text-xl font-medium text-[#2E3A3F]">
              Please select a child to view or send messages.
            </h2>
            <p className="text-sm text-[#6F828A] mt-2 leading-relaxed">
              Messages are organised by child so conversations stay clear, secure, and easy to export.
            </p>
            <div className="mt-5 flex justify-center">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    className="bg-[#1F9FA4] hover:bg-[#178085] text-white rounded-sm h-10 px-5"
                    data-testid="messages-choose-child-button"
                  >
                    <Users className="w-4 h-4 mr-2" strokeWidth={1.75} /> Choose a child
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="center"
                  sideOffset={6}
                  className="w-64 bg-white border-[#D9E1E3]"
                  data-testid="messages-choose-child-menu"
                >
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] font-medium">
                    Pick a child
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-[#D9E1E3]" />
                  {(scopeChildren || []).map((c) => (
                    <DropdownMenuItem
                      key={c.id}
                      onSelect={() => setSelectedChildId(c.id)}
                      className="cursor-pointer focus:bg-[#EEF3F4]"
                      data-testid={`messages-pick-child-${c.id}`}
                    >
                      <span
                        className="inline-block w-2.5 h-2.5 rounded-full mr-2.5 flex-shrink-0"
                        style={{ backgroundColor: c.color || "#1F9FA4" }}
                        aria-hidden="true"
                      />
                      <span className="flex-1 text-sm text-[#2E3A3F]">{c.name}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      ) : (
        <>
      {/* Permanence banner — desktop / tablet only. Mobile shows the
          compact "Permanent" pill in the title row instead. */}
      <div className="hidden sm:flex items-center gap-2 text-xs text-[#6F828A] bg-[#EEF3F4] border border-[#D9E1E3] px-3 py-2 rounded-sm">
        <Lock className="w-3.5 h-3.5" strokeWidth={1.5} />
        Messages are permanent. They cannot be edited or deleted once sent.
      </div>

      {/* In-thread search — filters both tabs via filteredMsgs / filteredFlagged */}
      <div className="relative" data-testid="messages-search-wrap">
        <Search className="w-4 h-4 text-[#6F828A] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" strokeWidth={1.5} />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search in messages…"
          className="pl-9 pr-9 h-9 sm:h-10 border-[#D9E1E3] rounded-sm bg-white"
          data-testid="messages-search-input"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#6F828A] hover:text-[#2E3A3F]"
            aria-label="Clear search"
            data-testid="messages-search-clear"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <Tabs defaultValue="all" className="flex-1 flex flex-col min-h-0">
        <TabsList className="bg-[#E8F0F1] rounded-sm self-start h-8 sm:h-10">
          <TabsTrigger value="all" className="text-xs sm:text-sm px-2 sm:px-3 h-7 sm:h-9" data-testid="tab-all-messages">All messages</TabsTrigger>
          <TabsTrigger value="flagged" className="text-xs sm:text-sm px-2 sm:px-3 h-7 sm:h-9" data-testid="tab-flagged-messages">Flagged {flagged.length > 0 && <span className="ml-1 text-xs bg-[#825A41] text-white px-1.5 rounded-sm">{flagged.length}</span>}</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="flex-1 flex flex-col min-h-0 mt-2 sm:mt-4 data-[state=inactive]:hidden">
          <div ref={listRef} className="flex-1 min-h-0 border border-[#D9E1E3] bg-white rounded-md overflow-y-auto overflow-x-hidden p-3 sm:p-4 space-y-3 sm:space-y-4" data-testid="messages-list">
            {msgs.length === 0 && (
              <div className="h-full flex items-center justify-center text-[#6F828A] text-sm">No messages yet. Start the conversation below.</div>
            )}
            {msgs.length > 0 && filteredMsgs.length === 0 && (
              <div className="h-full flex flex-col items-center justify-center text-[#6F828A] text-sm gap-2" data-testid="messages-empty-search">
                <Search className="w-5 h-5" strokeWidth={1.5} />
                <div>No matches for <span className="font-medium text-[#2E3A3F]">"{search.trim()}"</span>.</div>
                <button type="button" onClick={() => setSearch("")} className="text-xs underline text-[#1F9FA4] hover:text-[#178085]" data-testid="messages-empty-search-clear">Clear search</button>
              </div>
            )}
            {filteredMsgs.map((m) => renderMessage(m))}
          </div>
          <form onSubmit={send} className={`${isMediator ? "hidden" : "flex flex-col"} gap-2 mt-2 sm:mt-3 pt-2 sm:pt-3 pb-[env(safe-area-inset-bottom)] border-t border-[#D9E1E3] bg-[#F5F7F8] flex-shrink-0`}>
            <UpgradePrompt feature="send messages" testid="messages-upgrade-prompt" />
            {/* Phase 3 Delivery B — explicit child selection on send.
                v77.21.8 — Pass an empty hint on mobile to keep the
                composer compact; the label alone is enough. The hint
                is restored on tablet/desktop for clarity. */}
            {(scopeChildren || []).length > 0 && !selectedChildId && (
              <div className="[&_p]:hidden sm:[&_p]:block">
                <ChildPicker
                  value={msgChildIds}
                  onChange={setMsgChildIds}
                  testPrefix="msg-child-picker"
                  label="Send under which child?"
                  hint="Picking a child means only co-parents linked to that child will see this message."
                />
              </div>
            )}
            {toneHints.length > 0 && (
              <div className="border border-[#E8C29A] bg-[#FBF0DC] text-[#5C3A1E] rounded-sm p-3 text-xs space-y-1" data-testid="tone-warning-banner">
                <div className="flex items-center gap-1.5 font-medium uppercase tracking-[0.08em] text-[10px]">
                  <Info className="w-3.5 h-3.5" strokeWidth={1.8} /> Tone suggestion
                </div>
                {toneHints.map((h, i) => <div key={`${h}-${i}`}>· {h}</div>)}
                <div className="text-[#8A6A3B] italic mt-1">This is a private, silent nudge. Your co-parent sees nothing. Send anyway if this is accurate.</div>
              </div>
            )}
            <div className="flex gap-2 items-end">
              <Textarea ref={textareaRef} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write a factual, neutral message…"
                rows={1}
                className="flex-1 min-w-0 min-h-[44px] sm:min-h-[56px] md:min-h-[72px] max-h-[144px] border-[#D9E1E3] rounded-sm resize-none transition-[height] duration-150" data-testid="message-input"
                onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(e); }} />
              <Button type="submit" disabled={busy || !body.trim()} className="bg-[#1F9FA4] hover:bg-[#178085] rounded-sm h-[44px] sm:h-[56px] md:h-[72px] w-11 sm:w-12 flex-shrink-0 px-0" data-testid="message-send-button" aria-label="Send message">
                <Send className="w-4 h-4" strokeWidth={1.5} />
              </Button>
            </div>
            {/* v77.99.53 — Voice-to-text composer.
                Re-uses the same browser-native Web Speech component
                as Journal entries. Transcribed text is appended to
                whatever the user has already typed (with a single
                joining space when needed) so the draft is never
                clobbered, and never auto-sent. Sits beneath the
                textarea so it never crowds the send button on
                mobile. Helper text is intentionally one calm line. */}
            <div className="flex flex-wrap items-center gap-2 -mt-1">
              <VoiceDictateButton
                testidPrefix="message-voice"
                onTranscript={(text) =>
                  setBody((prev) => (prev ? prev + (prev.endsWith(" ") ? "" : " ") : "") + text)
                }
                disabled={busy}
              />
              <span className="text-[11px] text-[#6F828A] leading-snug">
                Use voice-to-text to draft your message. You can review and edit before sending.
              </span>
            </div>
          </form>
        </TabsContent>

        <TabsContent value="flagged" className="flex-1 overflow-y-auto mt-4 space-y-4 data-[state=inactive]:hidden" data-testid="flagged-list">
          {flagged.length === 0 && <div className="border border-[#D9E1E3] bg-white rounded-md p-8 text-center text-[#6F828A] text-sm">No flagged messages.</div>}
          {flagged.length > 0 && filteredFlagged.length === 0 && (
            <div className="border border-[#D9E1E3] bg-white rounded-md p-8 text-center text-[#6F828A] text-sm" data-testid="flagged-empty-search">
              No flagged matches for <span className="font-medium text-[#2E3A3F]">"{search.trim()}"</span>.{" "}
              <button type="button" onClick={() => setSearch("")} className="underline text-[#1F9FA4] hover:text-[#178085]" data-testid="flagged-empty-search-clear">Clear search</button>
            </div>
          )}
          {filteredFlagged.map((m) => renderMessage(m, true))}
        </TabsContent>
      </Tabs>
        </>
      )}

      {/* Export PDF dialog — date range, sender, flagged-only, search-term passthrough */}
      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent preventOutsideClose className="bg-white border-[#D9E1E3] max-w-lg" data-testid="export-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Export messages to PDF</DialogTitle>
            <DialogDescription className="text-[#6F828A]">Immutable, time-stamped record for legal, mediation or personal use. You can filter what's included below.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] text-[#6F828A]">Date range</Label>
              <div className="grid grid-cols-4 gap-1 mt-2" role="radiogroup" aria-label="Export date range">
                {[
                  { v: "full", label: "Full log" },
                  { v: "7d", label: "Last 7 days" },
                  { v: "30d", label: "Last 30 days" },
                  { v: "custom", label: "Custom" },
                ].map((opt) => {
                  const active = exportScope === opt.v;
                  return (
                    <button key={opt.v} type="button" role="radio" aria-checked={active}
                      onClick={() => setExportScope(opt.v)}
                      className={`px-2 py-2 text-xs border rounded-sm transition-colors ${active ? "bg-[#1F9FA4] border-[#1F9FA4] text-white" : "bg-white border-[#D9E1E3] text-[#2E3A3F] hover:bg-[#EEF3F4]"}`}
                      data-testid={`export-scope-${opt.v}`}>
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              {exportScope === "custom" && (
                <div className="grid grid-cols-2 gap-3 mt-3" data-testid="export-custom-range">
                  <div>
                    <Label className="text-xs text-[#6F828A]">From</Label>
                    <Input type="date" value={exportFrom} onChange={(e) => setExportFrom(e.target.value)} data-testid="export-from-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-[#6F828A]">To</Label>
                    <Input type="date" value={exportTo} onChange={(e) => setExportTo(e.target.value)} data-testid="export-to-input" />
                  </div>
                </div>
              )}
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.1em] text-[#6F828A]">Sender</Label>
              <Select value={exportSender} onValueChange={setExportSender}>
                <SelectTrigger className="mt-2" data-testid="export-sender-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="export-sender-all">Everyone</SelectItem>
                  {(members || []).map((m) => (
                    <SelectItem key={m.id} value={m.id} data-testid={`export-sender-${m.id}`}>{m.name}{m.id === user?.id ? " (you)" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <label className="flex items-center gap-2 text-sm text-[#2E3A3F] cursor-pointer select-none" data-testid="export-flagged-only-label">
              <input type="checkbox" checked={exportFlaggedOnly} onChange={(e) => setExportFlaggedOnly(e.target.checked)} className="w-4 h-4 rounded-sm border-[#D9E1E3] accent-[#1F9FA4]" data-testid="export-flagged-only-input" />
              Flagged messages only
            </label>

            {search.trim() && (
              <label className="flex items-start gap-2 text-sm text-[#2E3A3F] cursor-pointer select-none border border-[#E8C29A] bg-[#FBF0DC] rounded-sm p-3" data-testid="export-include-search-label">
                <input type="checkbox" checked={exportIncludeSearch} onChange={(e) => setExportIncludeSearch(e.target.checked)} className="w-4 h-4 rounded-sm border-[#E8C29A] accent-[#1F9FA4] mt-0.5" data-testid="export-include-search-input" />
                <span>
                  Only include messages matching your current search: <span className="font-mono-data">"{search.trim()}"</span>
                  <span className="block text-xs text-[#8A6A3B] mt-0.5">Untick to export without this filter.</span>
                </span>
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setExportOpen(false)} data-testid="export-cancel-button">Cancel</Button>
            <Button onClick={doExport} className="bg-[#1F9FA4] hover:bg-[#178085]" data-testid="export-confirm-button"><Download className="w-4 h-4 mr-2" strokeWidth={1.5} /> Download PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!flagFor} onOpenChange={(v) => !v && setFlagFor(null)}>
        <DialogContent preventOutsideClose className="bg-white border-[#D9E1E3]">
          <DialogHeader><DialogTitle className="font-heading">Flag as concerning</DialogTitle></DialogHeader>
          <p className="text-sm text-[#6F828A]">Flagging creates a permanent marker on this message. Optionally add a short reason.</p>
          <Input value={flagReason} onChange={(e) => setFlagReason(e.target.value)} placeholder="Reason (optional)" data-testid="flag-reason-input" />
          <DialogFooter>
            <Button onClick={submitFlag} className="bg-[#825A41] hover:bg-[#6d4935] text-white" data-testid="flag-submit-button">Flag</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
